import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, ListChecks, LogOut, ChevronsLeft, ChevronsRight, Search, Gavel, ShieldAlert, Mail, Database, ExternalLink, Settings, Building2, Landmark, TrendingUp } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const COLLAPSE_KEY = 'bankably_admin_sidebar_collapsed';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const [search, setSearch] = useState('');

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      localStorage.setItem(COLLAPSE_KEY, prev ? '0' : '1');
      return !prev;
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) navigate(`/admin/users?q=${encodeURIComponent(search.trim())}`);
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition ${
      isActive ? 'bg-gold/10 text-gold' : 'text-textSecondary hover:bg-surface2 hover:text-textPrimary'
    }`;

  const navItem = (to: string, end: boolean, icon: React.ReactNode, label: string) => (
    <NavLink to={to} end={end} className={linkClass}>
      {({ isActive }: { isActive: boolean }) => (
        <>
          {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-gold" />}
          {icon}
          {!collapsed && <span>{label}</span>}
        </>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-bg flex">
      <aside className={`${collapsed ? 'w-20' : 'w-64'} bg-surface1 border-r border-border flex flex-col transition-all duration-200`}>
        <div className="px-5 py-6 border-b border-border flex items-center justify-between">
          {!collapsed && (
            <div>
              <h1 className="font-display text-xl font-bold text-gold">Bankably</h1>
              <p className="text-textTertiary text-sm">Admin Dashboard</p>
            </div>
          )}
          <button
            onClick={toggleCollapsed}
            className="text-textTertiary hover:text-textPrimary p-1.5 rounded-md hover:bg-surface2 transition"
            aria-label="Toggle sidebar"
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        </div>

        {!collapsed && (
          <form onSubmit={handleSearch} className="px-3 pt-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-textTertiary" size={15} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-9 pr-3 py-2 bg-surface2 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-gold"
              />
            </div>
          </form>
        )}

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItem('/admin', true, <LayoutDashboard size={18} />, 'Overview')}
          {navItem('/admin/users', false, <Users size={18} />, 'Users')}
          {navItem('/admin/wishlist', false, <ListChecks size={18} />, 'Wishlist')}
          {navItem('/admin/mfaf-disputes', false, <Gavel size={18} />, 'MFAF Disputes')}
          {navItem('/admin/credit-protect-claims', false, <ShieldAlert size={18} />, 'Credit Protect Claims')}
          {navItem('/admin/business-verifications', false, <Building2 size={18} />, 'Business Verifications')}
          {navItem('/admin/business-credit-applications', false, <Landmark size={18} />, 'Business Credit Apps')}
          {navItem('/admin/credit-builder-applications', false, <TrendingUp size={18} />, 'Credit Builder Apps')}
          {navItem('/admin/settings', false, <Settings size={18} />, 'Settings')}

          <a
            href="/webmail"
            target="_blank"
            rel="noopener noreferrer"
            className="relative flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-textSecondary hover:bg-surface2 hover:text-textPrimary transition"
          >
            <Mail size={18} />
            {!collapsed && (
              <span className="flex items-center gap-1.5">
                Webmail
                <ExternalLink size={12} className="text-textTertiary" />
              </span>
            )}
          </a>

          <a
            href="/admin-db/"
            target="_blank"
            rel="noopener noreferrer"
            className="relative flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-textSecondary hover:bg-surface2 hover:text-textPrimary transition"
          >
            <Database size={18} />
            {!collapsed && (
              <span className="flex items-center gap-1.5">
                Database
                <ExternalLink size={12} className="text-textTertiary" />
              </span>
            )}
          </a>
        </nav>

        <div className="px-3 py-4 border-t border-border">
          {!collapsed && <p className="text-textTertiary text-xs px-4 mb-2 truncate">{user?.email}</p>}
          <button
            onClick={() => {
              logout();
              navigate('/');
            }}
            className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-textSecondary hover:bg-surface2 hover:text-textPrimary w-full transition"
          >
            <LogOut size={18} /> {!collapsed && 'Log Out'}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
